import type { AxiosError } from 'axios'

import type { ApisauceInstance } from '@mangovoice/apisauce'

import type { RateLimitInfo, RateLimitInterceptorConfig } from '../types'

const RATE_LIMITED_STATUS = 429

const DEFAULT_COALESCE_WINDOW_MS = 5 * 60 * 1000

// The window is global rather than per route: throttling applies to the whole
// client, so per-route windows would fan out reports for every route at once.
// Reports themselves are per route so analytics can break down by route value.

const stripQueryParams = (url: string): string => {
  const qIndex = url.indexOf('?')
  return qIndex >= 0 ? url.slice(0, qIndex) : url
}

// Ids can carry PHI or secrets (phone numbers, emailed capability tokens), so
// collapsing them keeps that out of the payload as well as keeping one
// endpoint on one route key. Kept deliberately over-eager: a collapsed static
// segment costs a little route resolution, a leaked id costs privacy.
const isEntitySegment = (segment: string): boolean =>
  /^\+?\d+$/.test(segment) ||
  /^[0-9a-fA-F-]{16,}$/.test(segment) ||
  /%[0-9a-fA-F]{2}/.test(segment) ||
  (segment.length >= 12 && /\d/.test(segment))

const normalizeRoute = (url: string): string =>
  stripQueryParams(url)
    .split('/')
    .map((segment) => (isEntitySegment(segment) ? '{id}' : segment))
    .join('/')

/**
 * Creates a rate limit response interceptor with configurable analytics callback.
 * Fires `onRateLimited` when a 429 response is encountered: once when throttling
 * starts, then at most one report per route per window for as long as it
 * continues. Query params and entity ids are stripped from the URL before
 * passing to the callback (PHI protection).
 *
 * Each report carries its own increment, not a running total — sum `count`
 * across reports for the true number of 429s.
 *
 * The returned `flush` reports anything still suppressed and closes the
 * window; wire it to `pagehide` (or app quit) so a reload mid-window does not
 * drop the counts.
 */
export const createRateLimitInterceptor = (
  config: RateLimitInterceptorConfig = {},
) => {
  const windowMs = config.coalesceWindowMs ?? DEFAULT_COALESCE_WINDOW_MS
  // Null means no window is open; a record means one is, holding the routes
  // suppressed so far.
  let suppressedRoutes: Record<string, number> | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const report = (route: string, count: number) => {
    const info: RateLimitInfo = {
      url: route,
      status: RATE_LIMITED_STATUS,
      count,
    }
    // A failing analytics sink must not break request handling or window
    // bookkeeping.
    try {
      config.onRateLimited?.(info)
    } catch {
      // Swallowed deliberately.
    }
  }

  const reportSuppressed = (routes: Record<string, number>) => {
    Object.entries(routes).forEach(([route, count]) => report(route, count))
  }

  const openWindow = () => {
    suppressedRoutes = {}
    timer = setTimeout(() => {
      const routes = suppressedRoutes ?? {}
      if (Object.keys(routes).length > 0) {
        reportSuppressed(routes)
        // Still being throttled, so stay coalesced. Closing here instead would
        // let the next 429 fire a leading report right behind this summary.
        openWindow()
      } else {
        suppressedRoutes = null
        timer = null
      }
    }, windowMs)
  }

  const flush = () => {
    if (suppressedRoutes === null) {
      return
    }
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    const routes = suppressedRoutes
    suppressedRoutes = null
    reportSuppressed(routes)
  }

  const responseErrorInterceptor = (error: AxiosError) => {
    const responseCode = error.response?.status ?? 0
    if (responseCode === RATE_LIMITED_STATUS && config.onRateLimited) {
      const route = normalizeRoute(error.config?.url ?? 'unknown_url')
      if (suppressedRoutes === null) {
        openWindow()
        report(route, 1)
      } else {
        suppressedRoutes[route] = (suppressedRoutes[route] ?? 0) + 1
      }
    }
    return Promise.reject(error)
  }

  return { responseErrorInterceptor, flush }
}

/**
 * Attaches rate limit interceptors to the given apisauce instances.
 * Legacy-compatible API matching web-app's `setRateLimitInterceptors`.
 */
export const setRateLimitInterceptors = (
  instances: ApisauceInstance[],
  config: RateLimitInterceptorConfig = {},
) => {
  // One interceptor for every instance, so coalescing spans them all.
  const { responseErrorInterceptor, flush } = createRateLimitInterceptor(config)
  instances.forEach((instance) => {
    instance.axiosInstance.interceptors.response.use(
      undefined,
      responseErrorInterceptor,
    )
  })
  return { flush }
}
